// Copyright 2024 Vajra. All rights reserved.
// Samyoga Security Analytics Dashboard

import { useState, useEffect } from 'react';
import {
  ShieldExclamationIcon,
  CloudIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  SparklesIcon,
  LockClosedIcon,
  GlobeAltIcon,
  MapIcon,
  BoltIcon,
  ShieldCheckIcon,
  CubeIcon,
  KeyIcon,
  UserGroupIcon,
  ClockIcon,
  ExclamationCircleIcon,
  DocumentCheckIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserCircleIcon,
  EyeIcon,
  ArrowPathIcon,
  MapPinIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { apiClient } from '../lib/api';

interface ThreatIntelStats {
  totalLookups: number;
  maliciousFound: number;
  cacheHitRate: number;
  avgLatencyMs: number;
  topThreats: Array<{ domain: string; count: number; severity: string }>;
}

interface AISPMStats {
  totalChecks: number;
  injectionBlocked: number;
  jailbreaksDetected: number;
  avgLatencyMs: number;
  topServices: Array<{ name: string; interactions: number; blocked: number }>;
}

interface M365Stats {
  tenantRiskScore: number;
  secureScore: number;
  criticalFindings: number;
  highFindings: number;
  mediumFindings: number;
  topIssues: Array<{ title: string; severity: string; category: string }>;
}

interface CTEMStats {
  attackPaths: number;
  highRiskPaths: number;
  breachLikelihood: number;
  crownJewelsAtRisk: number;
  avgRiskScore: number;
  mitreCoverage: number;
}

interface AttackPath {
  id: string;
  name: string;
  description: string;
  overallRisk: number;
  breachLikelihood: number;
  timeToCompromise: string;
  crownJewelsAtRisk: string[];
  nodes: Array<{
    id: string;
    name: string;
    type: string;
    riskScore: number;
    controls: string[];
  }>;
  edges: Array<{
    source: string;
    target: string;
    technique: string;
    mitreId: string;
    likelihood: number;
    mitigated: boolean;
  }>;
}

interface CrownJewel {
  id: string;
  name: string;
  type: string;
  criticality: string;
  riskScore: number;
  protectionStatus: string;
  attackPathsTo: number;
  browserExposure: string;
  controls: Array<{
    name: string;
    status: string;
    effectiveness: number;
  }>;
}

// NHI Types
type NHIType =
  | 'api_key' | 'aws_credential' | 'gcp_credential' | 'azure_credential'
  | 'github_token' | 'gitlab_token' | 'slack_token' | 'jwt' | 'oauth_token'
  | 'database_cred' | 'ssh_key' | 'certificate' | 'webhook_secret'
  | 'encryption_key' | 'service_account' | 'bot_token' | 'unknown';

type NHIStatus = 'active' | 'inactive' | 'orphaned' | 'expired' | 'rotating' | 'compromised';
type NHIRiskLevel = 'low' | 'medium' | 'high' | 'critical';

interface NHIRecord {
  id: string;
  name: string;
  type: NHIType;
  status: NHIStatus;
  riskLevel: NHIRiskLevel;
  riskScore: number;
  keyPrefix: string;
  keySuffix: string;
  fingerprint: string;
  provider: string;
  ownerUserId?: string;
  ownerEmail?: string;
  ownerTeam?: string;
  usageCount: number;
  firstSeen: string;
  lastSeen: string;
  usedByUsers: string[];
  usedOnDevices: string[];
  rotationAgeDays: number;
  hasMfa: boolean;
  isAdmin: boolean;
  permissions: string[];
  riskFactors: string[];
  alerted: boolean;
  lastAlertReason?: string;
}

interface NHIStats {
  totalNhis: number;
  activeNhis: number;
  orphanedNhis: number;
  expiredNhis: number;
  compromisedNhis: number;
  highRiskNhis: number;
  detectionsToday: number;
  blockedToday: number;
  byType: Array<{ type: string; count: number }>;
  byProvider: Array<{ provider: string; count: number }>;
  riskDistribution: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

// Compliance Types
type ComplianceFramework = 'soc2' | 'hipaa' | 'gdpr' | 'pci_dss';
type ControlStatus = 'compliant' | 'partial' | 'non_compliant' | 'not_applicable';

interface ComplianceFrameworkSummary {
  framework: ComplianceFramework;
  name: string;
  score: number;
  status: string;
  controlsTotal: number;
  controlsCompliant: number;
  openFindings: number;
  criticalFindings: number;
}

interface ComplianceControl {
  id: string;
  name: string;
  description: string;
  framework: ComplianceFramework;
  category: string;
  status: ControlStatus;
  score: number;
  evidenceSources: string[];
  findings: ComplianceFinding[];
  lastAssessed: string;
  nextAssessment: string;
}

interface ComplianceFinding {
  id: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  source: string;
  remediation: string;
  dueDate?: string;
  assignee?: string;
  status: 'open' | 'in_progress' | 'remediated' | 'accepted_risk';
  controlId?: string;
  controlName?: string;
  framework?: ComplianceFramework;
  category?: string;
}

interface ComplianceDashboard {
  overallScore: number;
  frameworks: ComplianceFrameworkSummary[];
  browserCompliance: {
    score: number;
    controlsCovered: number;
    features: Array<{ name: string; status: string; contribution: string }>;
  };
  sourceContribution: Array<{
    source: string;
    controlsCovered: number;
    avgScore: number;
    auditEntries: number;
  }>;
  recentActivity: Array<{
    timestamp: string;
    action: string;
    source: string;
    userId?: string;
    resourceType: string;
    resourceId: string;
    details: string;
    complianceRelevance: string[];
  }>;
  nextAssessments: Array<{
    controlId: string;
    controlName: string;
    framework: ComplianceFramework;
    nextAssessment: string;
  }>;
}

// UBA Types
type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
type AnomalyType =
  | 'impossible_travel'
  | 'unusual_access_time'
  | 'unusual_data_volume'
  | 'data_exfiltration'
  | 'privilege_escalation'
  | 'genai_sensitive_data'
  | 'nhi_misuse'
  | 'policy_violation';

type ThreatIndicator =
  | 'insider_threat'
  | 'compromised_account'
  | 'data_theft'
  | 'policy_circumvention'
  | 'negligent_behavior';

interface UserRiskProfile {
  userId: string;
  email: string;
  displayName: string;
  department: string;
  title: string;
  riskScore: number;
  riskLevel: RiskLevel;
  riskTrend: 'increasing' | 'stable' | 'decreasing';
  lastAssessed: string;
  behaviorBaseline: {
    avgLoginHour: number;
    typicalLocations: string[];
    typicalDevices: string[];
    typicalApplications: string[];
    avgDataVolumePerDay: number;
  };
  recentAnomalies: UserAnomaly[];
  threatIndicators: ThreatIndicator[];
  watchlistStatus: 'none' | 'monitoring' | 'investigation' | 'action_required';
}

interface UserAnomaly {
  id: string;
  userId: string;
  type: AnomalyType;
  severity: RiskLevel;
  timestamp: string;
  description: string;
  details: Record<string, unknown>;
  source: 'browser' | 'cloud' | 'identity' | 'network' | 'endpoint';
  status: 'new' | 'investigating' | 'resolved' | 'false_positive';
  assignee?: string;
  userName?: string;
  userEmail?: string;
  userDepartment?: string;
}

interface UBADashboard {
  totalUsers: number;
  usersAtRisk: number;
  criticalRiskUsers: number;
  activeAnomalies: number;
  resolvedToday: number;
  avgRiskScore: number;
  riskDistribution: Record<RiskLevel, number>;
  topRiskUsers: UserRiskProfile[];
  recentAnomalies: UserAnomaly[];
  threatIndicatorCounts: Record<ThreatIndicator, number>;
}

interface SamyogaMetrics {
  threatIntel: ThreatIntelStats;
  aiSpm: AISPMStats;
  m365: M365Stats;
  ctem: CTEMStats;
}

const defaultMetrics: SamyogaMetrics = {
  threatIntel: {
    totalLookups: 0,
    maliciousFound: 0,
    cacheHitRate: 0,
    avgLatencyMs: 0,
    topThreats: [],
  },
  aiSpm: {
    totalChecks: 0,
    injectionBlocked: 0,
    jailbreaksDetected: 0,
    avgLatencyMs: 0,
    topServices: [],
  },
  m365: {
    tenantRiskScore: 0,
    secureScore: 0,
    criticalFindings: 0,
    highFindings: 0,
    mediumFindings: 0,
    topIssues: [],
  },
  ctem: {
    attackPaths: 0,
    highRiskPaths: 0,
    breachLikelihood: 0,
    crownJewelsAtRisk: 0,
    avgRiskScore: 0,
    mitreCoverage: 0,
  },
};

export default function Samyoga() {
  const [metrics, setMetrics] = useState<SamyogaMetrics>(defaultMetrics);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'threats' | 'ai' | 'm365' | 'ctem' | 'nhi' | 'compliance' | 'uba'>('overview');
  const [attackPaths, setAttackPaths] = useState<AttackPath[]>([]);
  const [crownJewels, setCrownJewels] = useState<CrownJewel[]>([]);
  const [selectedPath, setSelectedPath] = useState<AttackPath | null>(null);
  const [nhiInventory, setNhiInventory] = useState<NHIRecord[]>([]);
  const [nhiStats, setNhiStats] = useState<NHIStats | null>(null);
  const [complianceDashboard, setComplianceDashboard] = useState<ComplianceDashboard | null>(null);
  const [complianceFindings, setComplianceFindings] = useState<ComplianceFinding[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<ComplianceFramework | null>(null);
  const [selectedNhi, setSelectedNhi] = useState<NHIRecord | null>(null);
  const [ubaDashboard, setUbaDashboard] = useState<UBADashboard | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserRiskProfile | null>(null);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      // In a real implementation, these would be actual API calls
      // For now, we simulate the data
      const simulatedMetrics: SamyogaMetrics = {
        threatIntel: {
          totalLookups: 15420,
          maliciousFound: 127,
          cacheHitRate: 94.2,
          avgLatencyMs: 12.3,
          topThreats: [
            { domain: 'malware-cdn.evil', count: 45, severity: 'critical' },
            { domain: 'phishing-attempt.fake', count: 32, severity: 'high' },
            { domain: 'suspicious-tracker.io', count: 28, severity: 'medium' },
            { domain: 'ad-network.spam', count: 22, severity: 'low' },
          ],
        },
        aiSpm: {
          totalChecks: 8932,
          injectionBlocked: 23,
          jailbreaksDetected: 5,
          avgLatencyMs: 18.7,
          topServices: [
            { name: 'ChatGPT', interactions: 4521, blocked: 12 },
            { name: 'Claude', interactions: 2344, blocked: 5 },
            { name: 'Gemini', interactions: 1234, blocked: 4 },
            { name: 'Copilot', interactions: 833, blocked: 2 },
          ],
        },
        m365: {
          tenantRiskScore: 35,
          secureScore: 68,
          criticalFindings: 0,
          highFindings: 2,
          mediumFindings: 5,
          topIssues: [
            {
              title: 'MFA not enforced for all users',
              severity: 'high',
              category: 'Identity Protection',
            },
            {
              title: 'External sharing without approval',
              severity: 'high',
              category: 'SharePoint/OneDrive',
            },
            {
              title: 'Legacy authentication enabled',
              severity: 'medium',
              category: 'Conditional Access',
            },
          ],
        },
        ctem: {
          attackPaths: 3,
          highRiskPaths: 2,
          breachLikelihood: 25,
          crownJewelsAtRisk: 2,
          avgRiskScore: 70,
          mitreCoverage: 85,
        },
      };
      setMetrics(simulatedMetrics);

      // Load CTEM attack paths
      const simulatedAttackPaths: AttackPath[] = [
        {
          id: 'AP-001',
          name: 'Browser Phishing to Domain Admin',
          description: 'Attacker phishes user via browser, steals credentials, escalates to domain admin',
          overallRisk: 78,
          breachLikelihood: 0.65,
          timeToCompromise: '4-6 hours',
          crownJewelsAtRisk: ['Active Directory', 'Customer Database'],
          nodes: [
            { id: 'n1', name: 'User Browser', type: 'browser', riskScore: 35, controls: ['Vajra DLP'] },
            { id: 'n2', name: 'Corporate Email', type: 'cloud', riskScore: 42, controls: ['Email Gateway'] },
            { id: 'n3', name: 'User Identity', type: 'identity', riskScore: 55, controls: ['MFA'] },
            { id: 'n4', name: 'Azure AD', type: 'identity', riskScore: 68, controls: ['PIM'] },
            { id: 'n5', name: 'Domain Controller', type: 'crown_jewel', riskScore: 85, controls: ['Tiered Admin'] },
          ],
          edges: [
            { source: 'n1', target: 'n2', technique: 'Phishing Link', mitreId: 'T1566', likelihood: 0.4, mitigated: true },
            { source: 'n2', target: 'n3', technique: 'Credential Harvest', mitreId: 'T1110', likelihood: 0.3, mitigated: true },
            { source: 'n3', target: 'n4', technique: 'Token Theft', mitreId: 'T1528', likelihood: 0.5, mitigated: false },
            { source: 'n4', target: 'n5', technique: 'Priv Escalation', mitreId: 'T1078', likelihood: 0.6, mitigated: false },
          ],
        },
        {
          id: 'AP-002',
          name: 'GenAI Data Exfiltration',
          description: 'User pastes sensitive data into AI, data exfiltrated via prompt injection',
          overallRisk: 62,
          breachLikelihood: 0.45,
          timeToCompromise: '< 1 hour',
          crownJewelsAtRisk: ['Source Code', 'API Keys'],
          nodes: [
            { id: 'n1', name: 'User Browser', type: 'browser', riskScore: 30, controls: ['GenAI DLP'] },
            { id: 'n2', name: 'ChatGPT', type: 'cloud', riskScore: 45, controls: ['AI-SPM'] },
            { id: 'n3', name: 'Sensitive Data', type: 'data', riskScore: 70, controls: ['DLP'] },
          ],
          edges: [
            { source: 'n3', target: 'n1', technique: 'Copy/Paste', mitreId: 'T1115', likelihood: 0.6, mitigated: true },
            { source: 'n1', target: 'n2', technique: 'Paste to AI', mitreId: 'T1567', likelihood: 0.4, mitigated: true },
          ],
        },
        {
          id: 'AP-003',
          name: 'Supply Chain via Extension',
          description: 'Malicious browser extension steals session tokens',
          overallRisk: 71,
          breachLikelihood: 0.35,
          timeToCompromise: '2-4 hours',
          crownJewelsAtRisk: ['SaaS Apps', 'Customer Data'],
          nodes: [
            { id: 'n1', name: 'Malicious Extension', type: 'browser', riskScore: 80, controls: ['Allowlist'] },
            { id: 'n2', name: 'Session Cookies', type: 'browser', riskScore: 65, controls: ['Isolation'] },
            { id: 'n3', name: 'Salesforce', type: 'cloud', riskScore: 55, controls: ['IP Restrict'] },
            { id: 'n4', name: 'Customer DB', type: 'crown_jewel', riskScore: 88, controls: ['Encryption'] },
          ],
          edges: [
            { source: 'n1', target: 'n2', technique: 'Cookie Theft', mitreId: 'T1539', likelihood: 0.3, mitigated: true },
            { source: 'n2', target: 'n3', technique: 'Session Hijack', mitreId: 'T1550', likelihood: 0.4, mitigated: false },
            { source: 'n3', target: 'n4', technique: 'Data Access', mitreId: 'T1213', likelihood: 0.5, mitigated: false },
          ],
        },
      ];
      setAttackPaths(simulatedAttackPaths);

      // Load crown jewels
      const simulatedCrownJewels: CrownJewel[] = [
        {
          id: 'cj-001',
          name: 'Active Directory',
          type: 'infrastructure',
          criticality: 'critical',
          riskScore: 45,
          protectionStatus: 'protected',
          attackPathsTo: 3,
          browserExposure: 'low',
          controls: [
            { name: 'Tiered Admin', status: 'enabled', effectiveness: 85 },
            { name: 'LAPS', status: 'enabled', effectiveness: 90 },
          ],
        },
        {
          id: 'cj-002',
          name: 'Customer Database',
          type: 'data',
          criticality: 'critical',
          riskScore: 62,
          protectionStatus: 'at_risk',
          attackPathsTo: 5,
          browserExposure: 'medium',
          controls: [
            { name: 'Encryption', status: 'enabled', effectiveness: 95 },
            { name: 'Data Masking', status: 'partial', effectiveness: 50 },
          ],
        },
        {
          id: 'cj-003',
          name: 'Source Code',
          type: 'intellectual_property',
          criticality: 'high',
          riskScore: 38,
          protectionStatus: 'protected',
          attackPathsTo: 2,
          browserExposure: 'high',
          controls: [
            { name: 'Branch Protection', status: 'enabled', effectiveness: 85 },
            { name: 'Secret Scanning', status: 'enabled', effectiveness: 90 },
          ],
        },
      ];
      setCrownJewels(simulatedCrownJewels);

      // Load NHI inventory
      const simulatedNhiInventory: NHIRecord[] = [
        {
          id: 'nhi-001',
          name: 'Production AWS Access Key',
          type: 'aws_credential',
          status: 'active',
          riskLevel: 'high',
          riskScore: 78,
          keyPrefix: 'AKIA',
          keySuffix: '7X9Z',
          fingerprint: 'sha256:a1b2c3...',
          provider: 'AWS',
          ownerUserId: 'user-123',
          ownerEmail: 'devops@company.com',
          ownerTeam: 'Platform Engineering',
          usageCount: 1247,
          firstSeen: '2024-06-15T10:00:00Z',
          lastSeen: new Date().toISOString(),
          usedByUsers: ['user-123', 'user-456'],
          usedOnDevices: ['device-001', 'device-002'],
          rotationAgeDays: 180,
          hasMfa: false,
          isAdmin: true,
          permissions: ['s3:*', 'ec2:*', 'lambda:*'],
          riskFactors: ['Multi-user access', 'Admin privileges', 'No MFA'],
          alerted: true,
          lastAlertReason: 'Browser access detected',
        },
        {
          id: 'nhi-002',
          name: 'GitHub Deploy Token',
          type: 'github_token',
          status: 'active',
          riskLevel: 'medium',
          riskScore: 52,
          keyPrefix: 'ghp_',
          keySuffix: 'mK9x',
          fingerprint: 'sha256:b2c3d4...',
          provider: 'GitHub',
          ownerUserId: 'user-789',
          ownerEmail: 'cicd@company.com',
          ownerTeam: 'DevOps',
          usageCount: 523,
          firstSeen: '2024-08-01T14:30:00Z',
          lastSeen: new Date().toISOString(),
          usedByUsers: ['user-789'],
          usedOnDevices: ['device-003'],
          rotationAgeDays: 90,
          hasMfa: true,
          isAdmin: false,
          permissions: ['repo', 'write:packages'],
          riskFactors: [],
          alerted: false,
        },
        {
          id: 'nhi-003',
          name: 'Slack Bot Token',
          type: 'slack_token',
          status: 'orphaned',
          riskLevel: 'critical',
          riskScore: 92,
          keyPrefix: 'xoxb',
          keySuffix: 'a1b2',
          fingerprint: 'sha256:c3d4e5...',
          provider: 'Slack',
          ownerUserId: 'user-departed-001',
          ownerEmail: 'former.employee@company.com',
          ownerTeam: 'Engineering',
          usageCount: 15,
          firstSeen: '2023-12-01T09:00:00Z',
          lastSeen: '2024-03-15T16:45:00Z',
          usedByUsers: ['user-departed-001'],
          usedOnDevices: ['device-old'],
          rotationAgeDays: 400,
          hasMfa: false,
          isAdmin: false,
          permissions: ['chat:write', 'channels:read'],
          riskFactors: ['Orphaned - owner departed', 'No activity 90+ days', 'Never rotated'],
          alerted: true,
          lastAlertReason: 'Orphaned credential detected',
        },
        {
          id: 'nhi-004',
          name: 'Azure Service Principal',
          type: 'azure_credential',
          status: 'active',
          riskLevel: 'high',
          riskScore: 71,
          keyPrefix: '7f3a',
          keySuffix: 'c9d8',
          fingerprint: 'sha256:d4e5f6...',
          provider: 'Azure',
          ownerUserId: 'user-456',
          ownerEmail: 'cloud@company.com',
          ownerTeam: 'Cloud Infrastructure',
          usageCount: 892,
          firstSeen: '2024-05-20T11:00:00Z',
          lastSeen: new Date().toISOString(),
          usedByUsers: ['user-456', 'user-789', 'user-101'],
          usedOnDevices: ['device-004', 'device-005', 'device-006'],
          rotationAgeDays: 120,
          hasMfa: true,
          isAdmin: false,
          permissions: ['Storage.Blob.Contributor', 'KeyVault.Secrets.Get'],
          riskFactors: ['Multi-user access (3 users)', 'Browser access detected'],
          alerted: true,
          lastAlertReason: 'Multi-user access detected',
        },
        {
          id: 'nhi-005',
          name: 'Database Connection String',
          type: 'database_cred',
          status: 'active',
          riskLevel: 'critical',
          riskScore: 88,
          keyPrefix: 'psql:',
          keySuffix: '@prod',
          fingerprint: 'sha256:e5f6g7...',
          provider: 'PostgreSQL',
          ownerUserId: 'user-dba-001',
          ownerEmail: 'dba@company.com',
          ownerTeam: 'Database Administration',
          usageCount: 2341,
          firstSeen: '2024-02-10T08:00:00Z',
          lastSeen: new Date().toISOString(),
          usedByUsers: ['user-dba-001'],
          usedOnDevices: ['device-007'],
          rotationAgeDays: 365,
          hasMfa: false,
          isAdmin: true,
          permissions: ['ALL PRIVILEGES'],
          riskFactors: ['Production database', 'Admin privileges', 'Long rotation period'],
          alerted: true,
          lastAlertReason: 'Production database credential in browser',
        },
        {
          id: 'nhi-006',
          name: 'JWT Signing Key',
          type: 'jwt',
          status: 'expired',
          riskLevel: 'medium',
          riskScore: 45,
          keyPrefix: 'eyJh',
          keySuffix: 'xYz9',
          fingerprint: 'sha256:f6g7h8...',
          provider: 'Internal Auth',
          ownerUserId: 'user-auth-001',
          ownerEmail: 'auth@company.com',
          ownerTeam: 'Security',
          usageCount: 0,
          firstSeen: '2024-01-01T00:00:00Z',
          lastSeen: '2024-06-30T23:59:59Z',
          usedByUsers: [],
          usedOnDevices: [],
          rotationAgeDays: 180,
          hasMfa: true,
          isAdmin: false,
          permissions: ['sign', 'verify'],
          riskFactors: ['Expired'],
          alerted: false,
        },
      ];
      setNhiInventory(simulatedNhiInventory);

      // Load NHI stats
      const simulatedNhiStats: NHIStats = {
        totalNhis: 6,
        activeNhis: 4,
        orphanedNhis: 1,
        expiredNhis: 1,
        compromisedNhis: 0,
        highRiskNhis: 4,
        detectionsToday: 23,
        blockedToday: 3,
        byType: [
          { type: 'aws_credential', count: 1 },
          { type: 'azure_credential', count: 1 },
          { type: 'github_token', count: 1 },
          { type: 'slack_token', count: 1 },
          { type: 'database_cred', count: 1 },
          { type: 'jwt', count: 1 },
        ],
        byProvider: [
          { provider: 'AWS', count: 1 },
          { provider: 'Azure', count: 1 },
          { provider: 'GitHub', count: 1 },
          { provider: 'Slack', count: 1 },
          { provider: 'PostgreSQL', count: 1 },
          { provider: 'Internal Auth', count: 1 },
        ],
        riskDistribution: {
          critical: 2,
          high: 2,
          medium: 2,
          low: 0,
        },
      };
      setNhiStats(simulatedNhiStats);

      // Load Compliance Dashboard
      const simulatedComplianceDashboard: ComplianceDashboard = {
        overallScore: 87,
        frameworks: [
          {
            framework: 'soc2',
            name: 'SOC 2',
            score: 90,
            status: 'compliant',
            controlsTotal: 4,
            controlsCompliant: 3,
            openFindings: 1,
            criticalFindings: 0,
          },
          {
            framework: 'hipaa',
            name: 'HIPAA',
            score: 90,
            status: 'compliant',
            controlsTotal: 4,
            controlsCompliant: 3,
            openFindings: 1,
            criticalFindings: 0,
          },
          {
            framework: 'gdpr',
            name: 'GDPR',
            score: 86,
            status: 'partial',
            controlsTotal: 4,
            controlsCompliant: 3,
            openFindings: 1,
            criticalFindings: 0,
          },
          {
            framework: 'pci_dss',
            name: 'PCI-DSS',
            score: 92,
            status: 'compliant',
            controlsTotal: 4,
            controlsCompliant: 3,
            openFindings: 1,
            criticalFindings: 0,
          },
        ],
        browserCompliance: {
          score: 94,
          controlsCovered: 12,
          features: [
            { name: 'DLP Protection', status: 'active', contribution: 'SOC2, HIPAA, GDPR, PCI-DSS' },
            { name: 'Threat Detection', status: 'active', contribution: 'SOC2, GDPR' },
            { name: 'Access Logging', status: 'active', contribution: 'SOC2, HIPAA, PCI-DSS' },
            { name: 'GenAI Monitoring', status: 'active', contribution: 'GDPR, SOC2' },
            { name: 'NHI Discovery', status: 'active', contribution: 'SOC2, PCI-DSS' },
          ],
        },
        sourceContribution: [
          { source: 'browser', controlsCovered: 12, avgScore: 94, auditEntries: 156 },
          { source: 'cloud', controlsCovered: 10, avgScore: 88, auditEntries: 89 },
          { source: 'identity', controlsCovered: 8, avgScore: 91, auditEntries: 67 },
          { source: 'network', controlsCovered: 4, avgScore: 96, auditEntries: 34 },
          { source: 'endpoint', controlsCovered: 3, avgScore: 85, auditEntries: 23 },
          { source: 'data', controlsCovered: 5, avgScore: 89, auditEntries: 45 },
        ],
        recentActivity: [
          {
            timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
            action: 'DLP_BLOCK',
            source: 'browser',
            userId: 'user-123',
            resourceType: 'clipboard',
            resourceId: 'clip-001',
            details: 'Blocked paste of credit card to unauthorized site',
            complianceRelevance: ['PCI-DSS-3.4', 'SOC2-CC6.7'],
          },
          {
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            action: 'ACCESS_GRANTED',
            source: 'identity',
            userId: 'user-456',
            resourceType: 'application',
            resourceId: 'salesforce',
            details: 'User authenticated via SSO',
            complianceRelevance: ['SOC2-CC6.1', 'HIPAA-164.312(a)'],
          },
          {
            timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
            action: 'THREAT_BLOCKED',
            source: 'browser',
            userId: 'user-789',
            resourceType: 'url',
            resourceId: 'phishing.example.com',
            details: 'Blocked phishing site access',
            complianceRelevance: ['SOC2-CC7.2', 'GDPR-Art32'],
          },
        ],
        nextAssessments: [
          {
            controlId: 'SOC2-CC6.7',
            controlName: 'Information Disposal',
            framework: 'soc2',
            nextAssessment: new Date(Date.now() + 27 * 24 * 60 * 60 * 1000).toISOString(),
          },
          {
            controlId: 'HIPAA-164.312(c)',
            controlName: 'Integrity Controls',
            framework: 'hipaa',
            nextAssessment: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(),
          },
        ],
      };
      setComplianceDashboard(simulatedComplianceDashboard);

      // Load Compliance Findings
      const simulatedFindings: ComplianceFinding[] = [
        {
          id: 'F-SOC2-001',
          severity: 'medium',
          title: 'Clipboard data not always sanitized',
          description: 'Browser DLP does not sanitize clipboard on tab close',
          source: 'browser',
          remediation: 'Enable clipboard sanitization policy for all managed tabs',
          status: 'in_progress',
          assignee: 'security-team@company.com',
          controlId: 'SOC2-CC6.7',
          controlName: 'Information Disposal',
          framework: 'soc2',
          category: 'Data Protection',
        },
        {
          id: 'F-HIPAA-001',
          severity: 'high',
          title: 'PHI screenshot protection not enforced',
          description: 'Screenshot protection not deployed to 15% of devices',
          source: 'endpoint',
          remediation: 'Deploy Vajra DLP policy to all managed endpoints',
          dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'open',
          assignee: 'compliance@company.com',
          controlId: 'HIPAA-164.312(c)',
          controlName: 'Integrity Controls',
          framework: 'hipaa',
          category: 'Technical Safeguards',
        },
        {
          id: 'F-GDPR-001',
          severity: 'medium',
          title: 'DPIA not completed for GenAI tools',
          description: 'Data protection impact assessment pending for ChatGPT and Claude',
          source: 'cloud',
          remediation: 'Complete DPIA for all GenAI services accessed via browser',
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'in_progress',
          assignee: 'privacy@company.com',
          controlId: 'GDPR-Art35',
          controlName: 'Data Protection Impact Assessment',
          framework: 'gdpr',
          category: 'Risk Assessment',
        },
        {
          id: 'F-PCI-001',
          severity: 'medium',
          title: 'Browser incident playbook not documented',
          description: 'Incident response procedures for browser security events need documentation',
          source: 'browser',
          remediation: 'Document browser-specific incident response procedures',
          dueDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'open',
          assignee: 'security-team@company.com',
          controlId: 'PCI-DSS-12.10',
          controlName: 'Incident Response Plan',
          framework: 'pci_dss',
          category: 'Incident Response',
        },
      ];
      setComplianceFindings(simulatedFindings);

      // Load UBA Dashboard
      const simulatedUbaDashboard: UBADashboard = {
        totalUsers: 5,
        usersAtRisk: 3,
        criticalRiskUsers: 1,
        activeAnomalies: 5,
        resolvedToday: 1,
        avgRiskScore: 59,
        riskDistribution: {
          low: 1,
          medium: 1,
          high: 2,
          critical: 1,
        },
        topRiskUsers: [
          {
            userId: 'user-102',
            email: 'sarah.jones@company.com',
            displayName: 'Sarah Jones',
            department: 'Sales',
            title: 'Account Executive',
            riskScore: 85,
            riskLevel: 'critical',
            riskTrend: 'increasing',
            lastAssessed: new Date().toISOString(),
            behaviorBaseline: {
              avgLoginHour: 8,
              typicalLocations: ['New York, US'],
              typicalDevices: ['Windows Laptop'],
              typicalApplications: ['Salesforce', 'Outlook'],
              avgDataVolumePerDay: 150,
            },
            recentAnomalies: [],
            threatIndicators: ['insider_threat', 'data_theft'],
            watchlistStatus: 'action_required',
          },
          {
            userId: 'user-101',
            email: 'john.smith@company.com',
            displayName: 'John Smith',
            department: 'Engineering',
            title: 'Senior Developer',
            riskScore: 78,
            riskLevel: 'high',
            riskTrend: 'increasing',
            lastAssessed: new Date().toISOString(),
            behaviorBaseline: {
              avgLoginHour: 9,
              typicalLocations: ['San Francisco, US'],
              typicalDevices: ['MacBook Pro'],
              typicalApplications: ['GitHub', 'Slack'],
              avgDataVolumePerDay: 250,
            },
            recentAnomalies: [],
            threatIndicators: ['data_theft', 'policy_circumvention'],
            watchlistStatus: 'investigation',
          },
          {
            userId: 'user-105',
            email: 'david.brown@company.com',
            displayName: 'David Brown',
            department: 'IT',
            title: 'System Administrator',
            riskScore: 62,
            riskLevel: 'high',
            riskTrend: 'stable',
            lastAssessed: new Date().toISOString(),
            behaviorBaseline: {
              avgLoginHour: 7,
              typicalLocations: ['Austin, US'],
              typicalDevices: ['Linux Workstation'],
              typicalApplications: ['AWS Console', 'Terraform'],
              avgDataVolumePerDay: 500,
            },
            recentAnomalies: [],
            threatIndicators: [],
            watchlistStatus: 'monitoring',
          },
        ],
        recentAnomalies: [
          {
            id: 'ANO-001',
            userId: 'user-102',
            type: 'impossible_travel',
            severity: 'critical',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            description: 'Login from London, UK 30 min after NY login',
            details: {},
            source: 'identity',
            status: 'investigating',
            assignee: 'security@company.com',
            userName: 'Sarah Jones',
            userEmail: 'sarah.jones@company.com',
            userDepartment: 'Sales',
          },
          {
            id: 'ANO-002',
            userId: 'user-102',
            type: 'data_exfiltration',
            severity: 'critical',
            timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
            description: 'Downloaded 2.5GB to personal cloud storage',
            details: {},
            source: 'browser',
            status: 'new',
            userName: 'Sarah Jones',
            userEmail: 'sarah.jones@company.com',
            userDepartment: 'Sales',
          },
          {
            id: 'ANO-003',
            userId: 'user-101',
            type: 'genai_sensitive_data',
            severity: 'high',
            timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
            description: 'Pasted source code with API keys to ChatGPT',
            details: {},
            source: 'browser',
            status: 'resolved',
            assignee: 'security@company.com',
            userName: 'John Smith',
            userEmail: 'john.smith@company.com',
            userDepartment: 'Engineering',
          },
          {
            id: 'ANO-004',
            userId: 'user-101',
            type: 'unusual_access_time',
            severity: 'medium',
            timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
            description: 'GitHub access at 3:00 AM (typical: 9 AM - 6 PM)',
            details: {},
            source: 'cloud',
            status: 'investigating',
            userName: 'John Smith',
            userEmail: 'john.smith@company.com',
            userDepartment: 'Engineering',
          },
          {
            id: 'ANO-006',
            userId: 'user-105',
            type: 'privilege_escalation',
            severity: 'high',
            timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
            description: 'Self-granted admin access to production AWS',
            details: {},
            source: 'cloud',
            status: 'investigating',
            assignee: 'security@company.com',
            userName: 'David Brown',
            userEmail: 'david.brown@company.com',
            userDepartment: 'IT',
          },
        ],
        threatIndicatorCounts: {
          insider_threat: 1,
          compromised_account: 0,
          data_theft: 2,
          policy_circumvention: 1,
          negligent_behavior: 1,
        },
      };
      setUbaDashboard(simulatedUbaDashboard);

    } catch (error) {
      console.error('Failed to load Samyoga metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-700 bg-red-50';
      case 'high':
        return 'text-orange-700 bg-orange-50';
      case 'medium':
        return 'text-yellow-700 bg-yellow-50';
      case 'low':
        return 'text-blue-700 bg-blue-50';
      default:
        return 'text-gray-700 bg-gray-50';
    }
  };

  const getRiskColor = (score: number) => {
    if (score >= 70) return 'text-red-600';
    if (score >= 50) return 'text-orange-600';
    if (score >= 30) return 'text-yellow-600';
    return 'text-green-600';
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: SparklesIcon },
    { id: 'threats', name: 'Threat Intelligence', icon: ShieldExclamationIcon },
    { id: 'ai', name: 'AI Security', icon: LockClosedIcon },
    { id: 'm365', name: 'M365 Posture', icon: CloudIcon },
    { id: 'ctem', name: 'Attack Paths', icon: MapIcon },
    { id: 'nhi', name: 'NHI Discovery', icon: KeyIcon },
    { id: 'compliance', name: 'Compliance', icon: ClipboardDocumentCheckIcon },
    { id: 'uba', name: 'User Behavior', icon: UserCircleIcon },
  ];

  const getRiskLevelColor = (level: RiskLevel) => {
    switch (level) {
      case 'critical': return 'text-red-700 bg-red-50';
      case 'high': return 'text-orange-700 bg-orange-50';
      case 'medium': return 'text-yellow-700 bg-yellow-50';
      case 'low': return 'text-green-700 bg-green-50';
      default: return 'text-gray-700 bg-gray-50';
    }
  };

  const getRiskScoreColor = (score: number) => {
    if (score >= 80) return 'text-red-600';
    if (score >= 60) return 'text-orange-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getAnomalyTypeLabel = (type: AnomalyType) => {
    const labels: Record<AnomalyType, string> = {
      impossible_travel: 'Impossible Travel',
      unusual_access_time: 'Unusual Time',
      unusual_data_volume: 'Data Volume',
      data_exfiltration: 'Data Exfiltration',
      privilege_escalation: 'Privilege Escalation',
      genai_sensitive_data: 'GenAI Sensitive Data',
      nhi_misuse: 'NHI Misuse',
      policy_violation: 'Policy Violation',
    };
    return labels[type] || type;
  };

  const getAnomalyStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'text-red-700 bg-red-50';
      case 'investigating': return 'text-yellow-700 bg-yellow-50';
      case 'resolved': return 'text-green-700 bg-green-50';
      case 'false_positive': return 'text-gray-700 bg-gray-50';
      default: return 'text-gray-700 bg-gray-50';
    }
  };

  const getWatchlistStatusColor = (status: string) => {
    switch (status) {
      case 'action_required': return 'text-red-700 bg-red-50';
      case 'investigation': return 'text-orange-700 bg-orange-50';
      case 'monitoring': return 'text-yellow-700 bg-yellow-50';
      case 'none': return 'text-gray-700 bg-gray-50';
      default: return 'text-gray-700 bg-gray-50';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing': return ArrowTrendingUpIcon;
      case 'decreasing': return ArrowTrendingDownIcon;
      default: return ArrowPathIcon;
    }
  };

  const getComplianceStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'text-green-700 bg-green-50';
      case 'partial': return 'text-yellow-700 bg-yellow-50';
      case 'non_compliant': return 'text-red-700 bg-red-50';
      default: return 'text-gray-700 bg-gray-50';
    }
  };

  const getComplianceScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getFrameworkIcon = (framework: ComplianceFramework) => {
    switch (framework) {
      case 'soc2': return ShieldCheckIcon;
      case 'hipaa': return DocumentCheckIcon;
      case 'gdpr': return LockClosedIcon;
      case 'pci_dss': return CubeIcon;
      default: return DocumentTextIcon;
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysUntil = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getNhiStatusColor = (status: NHIStatus) => {
    switch (status) {
      case 'active': return 'text-green-700 bg-green-50';
      case 'inactive': return 'text-gray-700 bg-gray-50';
      case 'orphaned': return 'text-orange-700 bg-orange-50';
      case 'expired': return 'text-yellow-700 bg-yellow-50';
      case 'rotating': return 'text-blue-700 bg-blue-50';
      case 'compromised': return 'text-red-700 bg-red-50';
      default: return 'text-gray-700 bg-gray-50';
    }
  };

  const getNhiTypeIcon = (type: NHIType) => {
    switch (type) {
      case 'aws_credential':
      case 'gcp_credential':
      case 'azure_credential':
        return CloudIcon;
      case 'github_token':
      case 'gitlab_token':
        return CubeIcon;
      case 'database_cred':
        return CubeIcon;
      case 'jwt':
      case 'oauth_token':
        return LockClosedIcon;
      default:
        return KeyIcon;
    }
  };

  const getNodeColor = (type: string) => {
    switch (type) {
      case 'browser': return 'bg-blue-100 border-blue-500 text-blue-700';
      case 'identity': return 'bg-purple-100 border-purple-500 text-purple-700';
      case 'cloud': return 'bg-cyan-100 border-cyan-500 text-cyan-700';
      case 'data': return 'bg-yellow-100 border-yellow-500 text-yellow-700';
      case 'network': return 'bg-gray-100 border-gray-500 text-gray-700';
      case 'crown_jewel': return 'bg-red-100 border-red-500 text-red-700';
      default: return 'bg-gray-100 border-gray-500 text-gray-700';
    }
  };

  const getProtectionColor = (status: string) => {
    switch (status) {
      case 'protected': return 'text-green-700 bg-green-50';
      case 'monitored': return 'text-yellow-700 bg-yellow-50';
      case 'at_risk': return 'text-red-700 bg-red-50';
      default: return 'text-gray-700 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Samyoga Security Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            AI-powered security insights across threat intelligence, GenAI protection, and M365 posture
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={loadMetrics}
            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
          >
            Refresh Data
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={clsx(
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                'group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium'
              )}
            >
              <tab.icon
                className={clsx(
                  activeTab === tab.id ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500',
                  '-ml-0.5 mr-2 h-5 w-5'
                )}
              />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="mt-6 space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <GlobeAltIcon className="h-8 w-8 text-blue-500" />
                </div>
                <div className="ml-4">
                  <dt className="truncate text-sm font-medium text-gray-500">Threat Lookups</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {metrics.threatIntel.totalLookups.toLocaleString()}
                  </dd>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
                </div>
                <div className="ml-4">
                  <dt className="truncate text-sm font-medium text-gray-500">Threats Blocked</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {metrics.threatIntel.maliciousFound}
                  </dd>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <LockClosedIcon className="h-8 w-8 text-purple-500" />
                </div>
                <div className="ml-4">
                  <dt className="truncate text-sm font-medium text-gray-500">AI Prompts Checked</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {metrics.aiSpm.totalChecks.toLocaleString()}
                  </dd>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CloudIcon className="h-8 w-8 text-green-500" />
                </div>
                <div className="ml-4">
                  <dt className="truncate text-sm font-medium text-gray-500">M365 Secure Score</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {metrics.m365.secureScore}%
                  </dd>
                </div>
              </div>
            </div>
          </div>

          {/* Integration Status */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Threat Intel Card */}
            <div className="overflow-hidden rounded-lg bg-white shadow">
              <div className="p-5">
                <div className="flex items-center">
                  <ShieldExclamationIcon className="h-6 w-6 text-blue-500" />
                  <h3 className="ml-2 text-lg font-medium text-gray-900">Threat Intelligence</h3>
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500">Cache Hit Rate</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {metrics.threatIntel.cacheHitRate}%
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Avg Latency</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {metrics.threatIntel.avgLatencyMs}ms
                    </dd>
                  </div>
                </dl>
                <div className="mt-4 flex items-center text-sm text-green-600">
                  <CheckCircleIcon className="h-5 w-5 mr-1" />
                  6 threat feeds active
                </div>
              </div>
            </div>

            {/* AI-SPM Card */}
            <div className="overflow-hidden rounded-lg bg-white shadow">
              <div className="p-5">
                <div className="flex items-center">
                  <SparklesIcon className="h-6 w-6 text-purple-500" />
                  <h3 className="ml-2 text-lg font-medium text-gray-900">AI Security (AI-SPM)</h3>
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500">Injections Blocked</dt>
                    <dd className="text-lg font-semibold text-red-600">
                      {metrics.aiSpm.injectionBlocked}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Jailbreaks Caught</dt>
                    <dd className="text-lg font-semibold text-red-600">
                      {metrics.aiSpm.jailbreaksDetected}
                    </dd>
                  </div>
                </dl>
                <div className="mt-4 flex items-center text-sm text-green-600">
                  <CheckCircleIcon className="h-5 w-5 mr-1" />
                  &lt;50ms latency target met
                </div>
              </div>
            </div>

            {/* M365 Card */}
            <div className="overflow-hidden rounded-lg bg-white shadow">
              <div className="p-5">
                <div className="flex items-center">
                  <CloudIcon className="h-6 w-6 text-green-500" />
                  <h3 className="ml-2 text-lg font-medium text-gray-900">M365 Security Posture</h3>
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500">Risk Score</dt>
                    <dd className={clsx('text-lg font-semibold', getRiskColor(metrics.m365.tenantRiskScore))}>
                      {metrics.m365.tenantRiskScore}/100
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Open Findings</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {metrics.m365.criticalFindings + metrics.m365.highFindings + metrics.m365.mediumFindings}
                    </dd>
                  </div>
                </dl>
                <div className="mt-4">
                  <div className="flex items-center space-x-2 text-sm">
                    {metrics.m365.criticalFindings > 0 && (
                      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                        {metrics.m365.criticalFindings} Critical
                      </span>
                    )}
                    {metrics.m365.highFindings > 0 && (
                      <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                        {metrics.m365.highFindings} High
                      </span>
                    )}
                    {metrics.m365.mediumFindings > 0 && (
                      <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                        {metrics.m365.mediumFindings} Medium
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Threat Intelligence Tab */}
      {activeTab === 'threats' && (
        <div className="mt-6 space-y-6">
          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="p-5">
              <h3 className="text-lg font-medium text-gray-900">Top Detected Threats</h3>
              <p className="text-sm text-gray-500 mt-1">
                Malicious domains and IPs blocked based on Samyoga threat intelligence
              </p>
              <ul className="mt-4 divide-y divide-gray-200">
                {metrics.threatIntel.topThreats.map((threat, idx) => (
                  <li key={idx} className="py-3 flex items-center justify-between">
                    <div className="flex items-center">
                      <ExclamationTriangleIcon
                        className={clsx(
                          'h-5 w-5 mr-3',
                          threat.severity === 'critical' ? 'text-red-500' :
                          threat.severity === 'high' ? 'text-orange-500' :
                          threat.severity === 'medium' ? 'text-yellow-500' : 'text-blue-500'
                        )}
                      />
                      <span className="font-medium text-gray-900">{threat.domain}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-500">{threat.count} blocks</span>
                      <span className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', getSeverityColor(threat.severity))}>
                        {threat.severity}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* AI Security Tab */}
      {activeTab === 'ai' && (
        <div className="mt-6 space-y-6">
          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="p-5">
              <h3 className="text-lg font-medium text-gray-900">GenAI Service Usage</h3>
              <p className="text-sm text-gray-500 mt-1">
                AI service interactions monitored by Samyoga AI-SPM
              </p>
              <table className="mt-4 min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Interactions</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Blocked</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Block Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {metrics.aiSpm.topServices.map((service, idx) => (
                    <tr key={idx}>
                      <td className="py-3 text-sm font-medium text-gray-900">{service.name}</td>
                      <td className="py-3 text-sm text-gray-500">{service.interactions.toLocaleString()}</td>
                      <td className="py-3 text-sm text-red-600 font-medium">{service.blocked}</td>
                      <td className="py-3 text-sm text-gray-500">
                        {((service.blocked / service.interactions) * 100).toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* M365 Posture Tab */}
      {activeTab === 'm365' && (
        <div className="mt-6 space-y-6">
          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="p-5">
              <h3 className="text-lg font-medium text-gray-900">M365 Security Findings</h3>
              <p className="text-sm text-gray-500 mt-1">
                Open security issues from Samyoga M365 security posture analysis
              </p>
              <ul className="mt-4 divide-y divide-gray-200">
                {metrics.m365.topIssues.map((issue, idx) => (
                  <li key={idx} className="py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{issue.title}</p>
                        <p className="mt-1 text-sm text-gray-500">{issue.category}</p>
                      </div>
                      <span className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', getSeverityColor(issue.severity))}>
                        {issue.severity}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* CTEM Attack Paths Tab */}
      {activeTab === 'ctem' && (
        <div className="mt-6 space-y-6">
          {/* CTEM Metrics */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <MapIcon className="h-8 w-8 text-orange-500" />
                </div>
                <div className="ml-4">
                  <dt className="truncate text-sm font-medium text-gray-500">Attack Paths</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {metrics.ctem.attackPaths}
                    <span className="ml-2 text-sm text-red-600">
                      ({metrics.ctem.highRiskPaths} high risk)
                    </span>
                  </dd>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BoltIcon className="h-8 w-8 text-red-500" />
                </div>
                <div className="ml-4">
                  <dt className="truncate text-sm font-medium text-gray-500">Breach Likelihood</dt>
                  <dd className={clsx('mt-1 text-2xl font-semibold', getRiskColor(metrics.ctem.breachLikelihood))}>
                    {metrics.ctem.breachLikelihood}%
                  </dd>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CubeIcon className="h-8 w-8 text-purple-500" />
                </div>
                <div className="ml-4">
                  <dt className="truncate text-sm font-medium text-gray-500">Crown Jewels at Risk</dt>
                  <dd className="mt-1 text-2xl font-semibold text-red-600">
                    {metrics.ctem.crownJewelsAtRisk}
                  </dd>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ShieldCheckIcon className="h-8 w-8 text-green-500" />
                </div>
                <div className="ml-4">
                  <dt className="truncate text-sm font-medium text-gray-500">MITRE Coverage</dt>
                  <dd className="mt-1 text-2xl font-semibold text-green-600">
                    {metrics.ctem.mitreCoverage}%
                  </dd>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Attack Paths List */}
            <div className="overflow-hidden rounded-lg bg-white shadow">
              <div className="p-5">
                <h3 className="text-lg font-medium text-gray-900">Discovered Attack Paths</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Browser-based attack paths to crown jewels
                </p>
                <ul className="mt-4 divide-y divide-gray-200">
                  {attackPaths.map((path) => (
                    <li
                      key={path.id}
                      className={clsx(
                        'py-4 cursor-pointer hover:bg-gray-50 -mx-5 px-5 transition-colors',
                        selectedPath?.id === path.id && 'bg-blue-50'
                      )}
                      onClick={() => setSelectedPath(path)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <MapIcon className={clsx(
                              'h-5 w-5 mr-2',
                              path.overallRisk >= 70 ? 'text-red-500' : path.overallRisk >= 50 ? 'text-orange-500' : 'text-yellow-500'
                            )} />
                            <p className="font-medium text-gray-900">{path.name}</p>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">{path.description}</p>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {path.crownJewelsAtRisk.map((cj, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                              >
                                {cj}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="ml-4 text-right">
                          <div className={clsx(
                            'text-lg font-bold',
                            path.overallRisk >= 70 ? 'text-red-600' : path.overallRisk >= 50 ? 'text-orange-600' : 'text-yellow-600'
                          )}>
                            {path.overallRisk}
                          </div>
                          <div className="text-xs text-gray-500">Risk Score</div>
                          <div className="mt-1 text-sm text-gray-600">{path.timeToCompromise}</div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Attack Path Visualization */}
            <div className="overflow-hidden rounded-lg bg-white shadow">
              <div className="p-5">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedPath ? selectedPath.name : 'Attack Path Visualization'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedPath ? `Breach likelihood: ${Math.round(selectedPath.breachLikelihood * 100)}%` : 'Select an attack path to visualize'}
                </p>

                {selectedPath ? (
                  <div className="mt-4">
                    {/* Node visualization */}
                    <div className="space-y-3">
                      {selectedPath.nodes.map((node, idx) => (
                        <div key={node.id} className="relative">
                          <div className={clsx(
                            'rounded-lg border-2 p-3',
                            getNodeColor(node.type)
                          )}>
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium">{node.name}</div>
                                <div className="text-xs opacity-75">{node.type}</div>
                              </div>
                              <div className="text-right">
                                <div className={clsx(
                                  'text-sm font-bold',
                                  node.riskScore >= 70 ? 'text-red-600' : node.riskScore >= 50 ? 'text-orange-600' : 'text-green-600'
                                )}>
                                  Risk: {node.riskScore}
                                </div>
                                <div className="text-xs">
                                  {node.controls.slice(0, 2).join(', ')}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Edge to next node */}
                          {idx < selectedPath.nodes.length - 1 && (
                            <div className="flex items-center justify-center py-1">
                              <div className="flex items-center">
                                <div className="h-4 w-0.5 bg-gray-300"></div>
                              </div>
                              <div className={clsx(
                                'ml-2 text-xs px-2 py-0.5 rounded',
                                selectedPath.edges[idx]?.mitigated ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              )}>
                                {selectedPath.edges[idx]?.technique} ({selectedPath.edges[idx]?.mitreId})
                                {selectedPath.edges[idx]?.mitigated && ' ✓'}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Legend */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="text-xs text-gray-500 mb-2">Node Types:</div>
                      <div className="flex flex-wrap gap-2">
                        {['browser', 'identity', 'cloud', 'data', 'crown_jewel'].map((type) => (
                          <span key={type} className={clsx('px-2 py-0.5 rounded text-xs border', getNodeColor(type))}>
                            {type.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-8 text-center text-gray-500">
                    <MapIcon className="h-12 w-12 mx-auto text-gray-300" />
                    <p className="mt-2">Click on an attack path to see the visualization</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Crown Jewels */}
          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="p-5">
              <h3 className="text-lg font-medium text-gray-900">Crown Jewels Protection Status</h3>
              <p className="text-sm text-gray-500 mt-1">
                Critical assets and their browser exposure risk
              </p>
              <table className="mt-4 min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Asset</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Browser Exposure</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Attack Paths</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {crownJewels.map((cj) => (
                    <tr key={cj.id}>
                      <td className="py-3">
                        <div className="flex items-center">
                          <CubeIcon className={clsx(
                            'h-5 w-5 mr-2',
                            cj.criticality === 'critical' ? 'text-red-500' : 'text-orange-500'
                          )} />
                          <span className="font-medium text-gray-900">{cj.name}</span>
                        </div>
                      </td>
                      <td className="py-3 text-sm text-gray-500">{cj.type}</td>
                      <td className="py-3">
                        <span className={clsx(
                          'font-bold',
                          cj.riskScore >= 60 ? 'text-red-600' : cj.riskScore >= 40 ? 'text-orange-600' : 'text-green-600'
                        )}>
                          {cj.riskScore}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={clsx(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                          getProtectionColor(cj.protectionStatus)
                        )}>
                          {cj.protectionStatus}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={clsx(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                          cj.browserExposure === 'high' ? 'text-red-700 bg-red-50' :
                          cj.browserExposure === 'medium' ? 'text-yellow-700 bg-yellow-50' :
                          'text-green-700 bg-green-50'
                        )}>
                          {cj.browserExposure}
                        </span>
                      </td>
                      <td className="py-3 text-sm text-gray-900">{cj.attackPathsTo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* NHI Discovery Tab */}
      {activeTab === 'nhi' && (
        <div className="mt-6 space-y-6">
          {/* NHI Stats */}
          {nhiStats && (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <KeyIcon className="h-8 w-8 text-blue-500" />
                  </div>
                  <div className="ml-4">
                    <dt className="truncate text-sm font-medium text-gray-500">Total NHIs</dt>
                    <dd className="mt-1 text-2xl font-semibold text-gray-900">
                      {nhiStats.totalNhis}
                      <span className="ml-2 text-sm text-green-600">
                        ({nhiStats.activeNhis} active)
                      </span>
                    </dd>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationCircleIcon className="h-8 w-8 text-orange-500" />
                  </div>
                  <div className="ml-4">
                    <dt className="truncate text-sm font-medium text-gray-500">Orphaned</dt>
                    <dd className="mt-1 text-2xl font-semibold text-orange-600">
                      {nhiStats.orphanedNhis}
                    </dd>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
                  </div>
                  <div className="ml-4">
                    <dt className="truncate text-sm font-medium text-gray-500">High Risk</dt>
                    <dd className="mt-1 text-2xl font-semibold text-red-600">
                      {nhiStats.highRiskNhis}
                    </dd>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ShieldExclamationIcon className="h-8 w-8 text-purple-500" />
                  </div>
                  <div className="ml-4">
                    <dt className="truncate text-sm font-medium text-gray-500">Blocked Today</dt>
                    <dd className="mt-1 text-2xl font-semibold text-gray-900">
                      {nhiStats.blockedToday}
                      <span className="ml-2 text-sm text-gray-500">
                        / {nhiStats.detectionsToday} detected
                      </span>
                    </dd>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* NHI Inventory List */}
            <div className="lg:col-span-2 overflow-hidden rounded-lg bg-white shadow">
              <div className="p-5">
                <h3 className="text-lg font-medium text-gray-900">Non-Human Identity Inventory</h3>
                <p className="text-sm text-gray-500 mt-1">
                  API keys, service accounts, and machine credentials discovered via browser
                </p>
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Credential</th>
                        <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                        <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                        <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Users</th>
                        <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Age</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {nhiInventory.map((nhi) => {
                        const TypeIcon = getNhiTypeIcon(nhi.type);
                        return (
                          <tr
                            key={nhi.id}
                            className={clsx(
                              'cursor-pointer hover:bg-gray-50 transition-colors',
                              selectedNhi?.id === nhi.id && 'bg-blue-50'
                            )}
                            onClick={() => setSelectedNhi(nhi)}
                          >
                            <td className="py-3">
                              <div className="flex items-center">
                                <TypeIcon className={clsx(
                                  'h-5 w-5 mr-2',
                                  nhi.riskLevel === 'critical' ? 'text-red-500' :
                                  nhi.riskLevel === 'high' ? 'text-orange-500' : 'text-blue-500'
                                )} />
                                <div>
                                  <div className="font-medium text-gray-900">{nhi.name}</div>
                                  <div className="text-xs text-gray-500">
                                    {nhi.keyPrefix}...{nhi.keySuffix}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 text-sm text-gray-500">{nhi.provider}</td>
                            <td className="py-3">
                              <span className={clsx(
                                'font-bold',
                                nhi.riskScore >= 80 ? 'text-red-600' :
                                nhi.riskScore >= 60 ? 'text-orange-600' :
                                nhi.riskScore >= 40 ? 'text-yellow-600' : 'text-green-600'
                              )}>
                                {nhi.riskScore}
                              </span>
                            </td>
                            <td className="py-3">
                              <span className={clsx(
                                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                getNhiStatusColor(nhi.status)
                              )}>
                                {nhi.status}
                              </span>
                            </td>
                            <td className="py-3">
                              {nhi.usedByUsers.length > 1 ? (
                                <span className="inline-flex items-center text-orange-600">
                                  <UserGroupIcon className="h-4 w-4 mr-1" />
                                  {nhi.usedByUsers.length}
                                </span>
                              ) : (
                                <span className="text-gray-500">1</span>
                              )}
                            </td>
                            <td className="py-3">
                              <span className={clsx(
                                'text-sm',
                                nhi.rotationAgeDays > 180 ? 'text-red-600 font-medium' :
                                nhi.rotationAgeDays > 90 ? 'text-orange-600' : 'text-gray-500'
                              )}>
                                {nhi.rotationAgeDays}d
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* NHI Details Panel */}
            <div className="overflow-hidden rounded-lg bg-white shadow">
              <div className="p-5">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedNhi ? 'Credential Details' : 'Select a Credential'}
                </h3>

                {selectedNhi ? (
                  <div className="mt-4 space-y-4">
                    {/* Risk Score */}
                    <div className="text-center p-4 rounded-lg bg-gray-50">
                      <div className={clsx(
                        'text-4xl font-bold',
                        selectedNhi.riskScore >= 80 ? 'text-red-600' :
                        selectedNhi.riskScore >= 60 ? 'text-orange-600' :
                        selectedNhi.riskScore >= 40 ? 'text-yellow-600' : 'text-green-600'
                      )}>
                        {selectedNhi.riskScore}
                      </div>
                      <div className="text-sm text-gray-500">Risk Score</div>
                      <span className={clsx(
                        'mt-2 inline-flex items-center rounded-full px-3 py-1 text-sm font-medium',
                        selectedNhi.riskLevel === 'critical' ? 'bg-red-100 text-red-800' :
                        selectedNhi.riskLevel === 'high' ? 'bg-orange-100 text-orange-800' :
                        selectedNhi.riskLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      )}>
                        {selectedNhi.riskLevel.toUpperCase()}
                      </span>
                    </div>

                    {/* Details */}
                    <dl className="divide-y divide-gray-200">
                      <div className="py-2 flex justify-between">
                        <dt className="text-sm text-gray-500">Type</dt>
                        <dd className="text-sm font-medium text-gray-900">{selectedNhi.type.replace('_', ' ')}</dd>
                      </div>
                      <div className="py-2 flex justify-between">
                        <dt className="text-sm text-gray-500">Provider</dt>
                        <dd className="text-sm font-medium text-gray-900">{selectedNhi.provider}</dd>
                      </div>
                      <div className="py-2 flex justify-between">
                        <dt className="text-sm text-gray-500">Owner</dt>
                        <dd className="text-sm font-medium text-gray-900">{selectedNhi.ownerEmail || 'Unknown'}</dd>
                      </div>
                      <div className="py-2 flex justify-between">
                        <dt className="text-sm text-gray-500">Team</dt>
                        <dd className="text-sm font-medium text-gray-900">{selectedNhi.ownerTeam || 'Unknown'}</dd>
                      </div>
                      <div className="py-2 flex justify-between">
                        <dt className="text-sm text-gray-500">Usage Count</dt>
                        <dd className="text-sm font-medium text-gray-900">{selectedNhi.usageCount.toLocaleString()}</dd>
                      </div>
                      <div className="py-2 flex justify-between">
                        <dt className="text-sm text-gray-500">Rotation Age</dt>
                        <dd className={clsx(
                          'text-sm font-medium',
                          selectedNhi.rotationAgeDays > 180 ? 'text-red-600' :
                          selectedNhi.rotationAgeDays > 90 ? 'text-orange-600' : 'text-gray-900'
                        )}>
                          {selectedNhi.rotationAgeDays} days
                        </dd>
                      </div>
                      <div className="py-2 flex justify-between">
                        <dt className="text-sm text-gray-500">MFA Enabled</dt>
                        <dd className="text-sm font-medium">
                          {selectedNhi.hasMfa ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          ) : (
                            <XCircleIcon className="h-5 w-5 text-red-500" />
                          )}
                        </dd>
                      </div>
                      <div className="py-2 flex justify-between">
                        <dt className="text-sm text-gray-500">Admin Access</dt>
                        <dd className="text-sm font-medium">
                          {selectedNhi.isAdmin ? (
                            <span className="text-red-600">Yes</span>
                          ) : (
                            <span className="text-gray-500">No</span>
                          )}
                        </dd>
                      </div>
                    </dl>

                    {/* Risk Factors */}
                    {selectedNhi.riskFactors.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Risk Factors</h4>
                        <ul className="space-y-1">
                          {selectedNhi.riskFactors.map((factor, idx) => (
                            <li key={idx} className="flex items-start text-sm">
                              <ExclamationTriangleIcon className="h-4 w-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600">{factor}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Permissions */}
                    {selectedNhi.permissions.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Permissions</h4>
                        <div className="flex flex-wrap gap-1">
                          {selectedNhi.permissions.map((perm, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                            >
                              {perm}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="pt-4 border-t border-gray-200 space-y-2">
                      <button
                        type="button"
                        className="w-full rounded-md bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-100"
                      >
                        <ClockIcon className="h-4 w-4 inline mr-1" />
                        Mark as Rotated
                      </button>
                      {selectedNhi.status === 'orphaned' && (
                        <button
                          type="button"
                          className="w-full rounded-md bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-600 hover:bg-blue-100"
                        >
                          <UserGroupIcon className="h-4 w-4 inline mr-1" />
                          Assign Owner
                        </button>
                      )}
                      <button
                        type="button"
                        className="w-full rounded-md bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-100"
                      >
                        <ExclamationCircleIcon className="h-4 w-4 inline mr-1" />
                        Mark Compromised
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-8 text-center text-gray-500">
                    <KeyIcon className="h-12 w-12 mx-auto text-gray-300" />
                    <p className="mt-2">Click on a credential to see details</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Risk Distribution */}
          {nhiStats && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="overflow-hidden rounded-lg bg-white shadow">
                <div className="p-5">
                  <h3 className="text-lg font-medium text-gray-900">Risk Distribution</h3>
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center">
                      <div className="w-24 text-sm text-gray-500">Critical</div>
                      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-red-500 rounded-full"
                          style={{ width: `${(nhiStats.riskDistribution.critical / nhiStats.totalNhis) * 100}%` }}
                        />
                      </div>
                      <div className="w-8 text-right text-sm font-medium text-gray-900">{nhiStats.riskDistribution.critical}</div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-24 text-sm text-gray-500">High</div>
                      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-orange-500 rounded-full"
                          style={{ width: `${(nhiStats.riskDistribution.high / nhiStats.totalNhis) * 100}%` }}
                        />
                      </div>
                      <div className="w-8 text-right text-sm font-medium text-gray-900">{nhiStats.riskDistribution.high}</div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-24 text-sm text-gray-500">Medium</div>
                      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-yellow-500 rounded-full"
                          style={{ width: `${(nhiStats.riskDistribution.medium / nhiStats.totalNhis) * 100}%` }}
                        />
                      </div>
                      <div className="w-8 text-right text-sm font-medium text-gray-900">{nhiStats.riskDistribution.medium}</div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-24 text-sm text-gray-500">Low</div>
                      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${(nhiStats.riskDistribution.low / nhiStats.totalNhis) * 100}%` }}
                        />
                      </div>
                      <div className="w-8 text-right text-sm font-medium text-gray-900">{nhiStats.riskDistribution.low}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg bg-white shadow">
                <div className="p-5">
                  <h3 className="text-lg font-medium text-gray-900">By Provider</h3>
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    {nhiStats.byProvider.map(({ provider, count }) => (
                      <div key={provider} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-900">{provider}</span>
                        <span className="text-lg font-bold text-blue-600">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && (
        <div className="mt-6 space-y-6">
          {/* Overall Compliance Score */}
          {complianceDashboard && (
            <>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
                <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                  <div className="text-center">
                    <div className={clsx(
                      'text-4xl font-bold',
                      getComplianceScoreColor(complianceDashboard.overallScore)
                    )}>
                      {complianceDashboard.overallScore}%
                    </div>
                    <div className="text-sm text-gray-500 mt-1">Overall Compliance</div>
                  </div>
                </div>

                {complianceDashboard.frameworks.map((fw) => {
                  const FrameworkIcon = getFrameworkIcon(fw.framework);
                  return (
                    <div
                      key={fw.framework}
                      className={clsx(
                        'overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6 cursor-pointer transition-all hover:shadow-lg',
                        selectedFramework === fw.framework && 'ring-2 ring-blue-500'
                      )}
                      onClick={() => setSelectedFramework(selectedFramework === fw.framework ? null : fw.framework)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <FrameworkIcon className={clsx(
                            'h-8 w-8',
                            fw.score >= 90 ? 'text-green-500' : fw.score >= 70 ? 'text-yellow-500' : 'text-red-500'
                          )} />
                          <div className="ml-3">
                            <div className="font-semibold text-gray-900">{fw.name}</div>
                            <span className={clsx(
                              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                              getComplianceStatusColor(fw.status)
                            )}>
                              {fw.status}
                            </span>
                          </div>
                        </div>
                        <div className={clsx(
                          'text-2xl font-bold',
                          getComplianceScoreColor(fw.score)
                        )}>
                          {fw.score}%
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        {fw.controlsCompliant}/{fw.controlsTotal} controls compliant
                        {fw.openFindings > 0 && (
                          <span className="ml-2 text-orange-600">
                            • {fw.openFindings} open finding{fw.openFindings > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Browser Compliance Contribution */}
                <div className="overflow-hidden rounded-lg bg-white shadow">
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900">Browser Compliance</h3>
                      <span className={clsx(
                        'text-2xl font-bold',
                        getComplianceScoreColor(complianceDashboard.browserCompliance.score)
                      )}>
                        {complianceDashboard.browserCompliance.score}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Vajra covers {complianceDashboard.browserCompliance.controlsCovered} compliance controls
                    </p>
                    <div className="mt-4 space-y-2">
                      {complianceDashboard.browserCompliance.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <div className="flex items-center">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                            <span className="text-gray-900">{feature.name}</span>
                          </div>
                          <span className="text-xs text-gray-500">{feature.contribution}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Evidence Sources */}
                <div className="overflow-hidden rounded-lg bg-white shadow">
                  <div className="p-5">
                    <h3 className="text-lg font-medium text-gray-900">Evidence Sources</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Compliance data from multiple sources
                    </p>
                    <div className="mt-4 space-y-2">
                      {complianceDashboard.sourceContribution.map((source) => (
                        <div key={source.source} className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="capitalize text-gray-900">{source.source}</span>
                              <span className={clsx('font-medium', getComplianceScoreColor(source.avgScore))}>
                                {source.avgScore}%
                              </span>
                            </div>
                            <div className="mt-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className={clsx(
                                  'h-full rounded-full',
                                  source.avgScore >= 90 ? 'bg-green-500' :
                                  source.avgScore >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                                )}
                                style={{ width: `${source.avgScore}%` }}
                              />
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {source.controlsCovered} controls • {source.auditEntries} audit entries
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Upcoming Assessments */}
                <div className="overflow-hidden rounded-lg bg-white shadow">
                  <div className="p-5">
                    <h3 className="text-lg font-medium text-gray-900">Upcoming Assessments</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Controls requiring reassessment
                    </p>
                    <ul className="mt-4 divide-y divide-gray-200">
                      {complianceDashboard.nextAssessments.map((assessment) => {
                        const daysUntil = getDaysUntil(assessment.nextAssessment);
                        return (
                          <li key={assessment.controlId} className="py-3">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{assessment.controlName}</p>
                                <p className="text-xs text-gray-500">{assessment.controlId}</p>
                              </div>
                              <div className="text-right">
                                <span className={clsx(
                                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                  daysUntil <= 7 ? 'bg-red-100 text-red-700' :
                                  daysUntil <= 30 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-green-100 text-green-700'
                                )}>
                                  <CalendarIcon className="h-3 w-3 mr-1" />
                                  {daysUntil}d
                                </span>
                                <p className="text-xs text-gray-500 mt-1">
                                  {formatDate(assessment.nextAssessment)}
                                </p>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Open Findings */}
              <div className="overflow-hidden rounded-lg bg-white shadow">
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Open Compliance Findings</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Issues requiring remediation across all frameworks
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                        {complianceFindings.filter(f => f.severity === 'high').length} High
                      </span>
                      <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                        {complianceFindings.filter(f => f.severity === 'medium').length} Medium
                      </span>
                    </div>
                  </div>
                  <table className="mt-4 min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Finding</th>
                        <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Framework</th>
                        <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                        <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                        <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {complianceFindings
                        .filter(f => !selectedFramework || f.framework === selectedFramework)
                        .map((finding) => (
                        <tr key={finding.id}>
                          <td className="py-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{finding.title}</p>
                              <p className="text-xs text-gray-500">{finding.controlId} - {finding.controlName}</p>
                            </div>
                          </td>
                          <td className="py-3">
                            <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                              {finding.framework?.toUpperCase().replace('_', '-')}
                            </span>
                          </td>
                          <td className="py-3">
                            <span className={clsx(
                              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                              finding.severity === 'critical' ? 'bg-red-100 text-red-800' :
                              finding.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                              finding.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-blue-100 text-blue-800'
                            )}>
                              {finding.severity}
                            </span>
                          </td>
                          <td className="py-3 text-sm text-gray-500 capitalize">{finding.source}</td>
                          <td className="py-3">
                            <span className={clsx(
                              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                              finding.status === 'open' ? 'bg-red-100 text-red-700' :
                              finding.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                              finding.status === 'remediated' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-700'
                            )}>
                              {finding.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="py-3 text-sm text-gray-500">
                            {finding.dueDate ? (
                              <span className={clsx(
                                getDaysUntil(finding.dueDate) <= 7 && 'text-red-600 font-medium'
                              )}>
                                {formatDate(finding.dueDate)}
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recent Compliance Activity */}
              <div className="overflow-hidden rounded-lg bg-white shadow">
                <div className="p-5">
                  <h3 className="text-lg font-medium text-gray-900">Audit Trail</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Recent compliance-relevant events across browser, cloud, and identity
                  </p>
                  <ul className="mt-4 divide-y divide-gray-200">
                    {complianceDashboard.recentActivity.map((event, idx) => (
                      <li key={idx} className="py-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start">
                            <span className={clsx(
                              'inline-flex items-center justify-center h-8 w-8 rounded-full',
                              event.source === 'browser' ? 'bg-blue-100 text-blue-600' :
                              event.source === 'identity' ? 'bg-purple-100 text-purple-600' :
                              event.source === 'cloud' ? 'bg-cyan-100 text-cyan-600' :
                              'bg-gray-100 text-gray-600'
                            )}>
                              {event.source === 'browser' ? (
                                <GlobeAltIcon className="h-4 w-4" />
                              ) : event.source === 'identity' ? (
                                <UserGroupIcon className="h-4 w-4" />
                              ) : (
                                <CloudIcon className="h-4 w-4" />
                              )}
                            </span>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">{event.action.replace('_', ' ')}</p>
                              <p className="text-sm text-gray-500">{event.details}</p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {event.complianceRelevance.map((ctrl, cidx) => (
                                  <span
                                    key={cidx}
                                    className="inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700"
                                  >
                                    {ctrl}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(event.timestamp).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* User Behavior Analytics Tab */}
      {activeTab === 'uba' && (
        <div className="mt-6 space-y-6">
          {ubaDashboard && (
            <>
              {/* UBA Metrics */}
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
                <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                  <div className="flex items-center">
                    <UserGroupIcon className="h-8 w-8 text-blue-500" />
                    <div className="ml-4">
                      <dt className="truncate text-sm font-medium text-gray-500">Total Users</dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">
                        {ubaDashboard.totalUsers}
                      </dd>
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-8 w-8 text-orange-500" />
                    <div className="ml-4">
                      <dt className="truncate text-sm font-medium text-gray-500">Users at Risk</dt>
                      <dd className="mt-1 text-2xl font-semibold text-orange-600">
                        {ubaDashboard.usersAtRisk}
                        <span className="ml-2 text-sm text-red-600">
                          ({ubaDashboard.criticalRiskUsers} critical)
                        </span>
                      </dd>
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                  <div className="flex items-center">
                    <BoltIcon className="h-8 w-8 text-red-500" />
                    <div className="ml-4">
                      <dt className="truncate text-sm font-medium text-gray-500">Active Anomalies</dt>
                      <dd className="mt-1 text-2xl font-semibold text-red-600">
                        {ubaDashboard.activeAnomalies}
                      </dd>
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-8 w-8 text-green-500" />
                    <div className="ml-4">
                      <dt className="truncate text-sm font-medium text-gray-500">Resolved Today</dt>
                      <dd className="mt-1 text-2xl font-semibold text-green-600">
                        {ubaDashboard.resolvedToday}
                      </dd>
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                  <div className="flex items-center">
                    <ShieldExclamationIcon className="h-8 w-8 text-purple-500" />
                    <div className="ml-4">
                      <dt className="truncate text-sm font-medium text-gray-500">Avg Risk Score</dt>
                      <dd className={clsx('mt-1 text-2xl font-semibold', getRiskScoreColor(ubaDashboard.avgRiskScore))}>
                        {ubaDashboard.avgRiskScore}
                      </dd>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Top Risk Users */}
                <div className="lg:col-span-2 overflow-hidden rounded-lg bg-white shadow">
                  <div className="p-5">
                    <h3 className="text-lg font-medium text-gray-900">Top Risk Users</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Users with highest behavioral risk scores
                    </p>
                    <div className="mt-4 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                            <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                            <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                            <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Trend</th>
                            <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {ubaDashboard.topRiskUsers.map((user) => {
                            const TrendIcon = getTrendIcon(user.riskTrend);
                            return (
                              <tr
                                key={user.userId}
                                className={clsx(
                                  'cursor-pointer hover:bg-gray-50 transition-colors',
                                  selectedUser?.userId === user.userId && 'bg-blue-50'
                                )}
                                onClick={() => setSelectedUser(selectedUser?.userId === user.userId ? null : user)}
                              >
                                <td className="py-3">
                                  <div className="flex items-center">
                                    <div className={clsx(
                                      'h-8 w-8 rounded-full flex items-center justify-center text-white font-medium',
                                      user.riskLevel === 'critical' ? 'bg-red-500' :
                                      user.riskLevel === 'high' ? 'bg-orange-500' :
                                      user.riskLevel === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                                    )}>
                                      {user.displayName.charAt(0)}
                                    </div>
                                    <div className="ml-3">
                                      <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
                                      <p className="text-xs text-gray-500">{user.email}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 text-sm text-gray-500">{user.department}</td>
                                <td className="py-3">
                                  <div className="flex items-center">
                                    <span className={clsx(
                                      'text-lg font-bold',
                                      getRiskScoreColor(user.riskScore)
                                    )}>
                                      {user.riskScore}
                                    </span>
                                    <span className={clsx(
                                      'ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                      getRiskLevelColor(user.riskLevel)
                                    )}>
                                      {user.riskLevel}
                                    </span>
                                  </div>
                                </td>
                                <td className="py-3">
                                  <TrendIcon className={clsx(
                                    'h-5 w-5',
                                    user.riskTrend === 'increasing' ? 'text-red-500' :
                                    user.riskTrend === 'decreasing' ? 'text-green-500' : 'text-gray-400'
                                  )} />
                                </td>
                                <td className="py-3">
                                  <span className={clsx(
                                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                    getWatchlistStatusColor(user.watchlistStatus)
                                  )}>
                                    {user.watchlistStatus.replace('_', ' ')}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* User Details or Risk Distribution */}
                <div className="overflow-hidden rounded-lg bg-white shadow">
                  <div className="p-5">
                    {selectedUser ? (
                      <>
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-medium text-gray-900">User Details</h3>
                          <button
                            onClick={() => setSelectedUser(null)}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            <XCircleIcon className="h-5 w-5" />
                          </button>
                        </div>

                        <div className="mt-4 text-center">
                          <div className={clsx(
                            'mx-auto h-16 w-16 rounded-full flex items-center justify-center text-white text-2xl font-bold',
                            selectedUser.riskLevel === 'critical' ? 'bg-red-500' :
                            selectedUser.riskLevel === 'high' ? 'bg-orange-500' :
                            selectedUser.riskLevel === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                          )}>
                            {selectedUser.displayName.charAt(0)}
                          </div>
                          <h4 className="mt-2 text-lg font-medium text-gray-900">{selectedUser.displayName}</h4>
                          <p className="text-sm text-gray-500">{selectedUser.title}</p>
                          <p className="text-sm text-gray-500">{selectedUser.department}</p>
                        </div>

                        <div className="mt-4 text-center p-4 rounded-lg bg-gray-50">
                          <div className={clsx(
                            'text-4xl font-bold',
                            getRiskScoreColor(selectedUser.riskScore)
                          )}>
                            {selectedUser.riskScore}
                          </div>
                          <div className="text-sm text-gray-500">Risk Score</div>
                        </div>

                        {selectedUser.threatIndicators.length > 0 && (
                          <div className="mt-4">
                            <h5 className="text-sm font-medium text-gray-900 mb-2">Threat Indicators</h5>
                            <div className="flex flex-wrap gap-1">
                              {selectedUser.threatIndicators.map((indicator, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                                >
                                  {indicator.replace('_', ' ')}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-4">
                          <h5 className="text-sm font-medium text-gray-900 mb-2">Behavior Baseline</h5>
                          <dl className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <dt className="text-gray-500">Typical Login</dt>
                              <dd className="text-gray-900">{selectedUser.behaviorBaseline.avgLoginHour}:00</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-gray-500">Locations</dt>
                              <dd className="text-gray-900 text-right">{selectedUser.behaviorBaseline.typicalLocations.join(', ')}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-gray-500">Avg Data/Day</dt>
                              <dd className="text-gray-900">{selectedUser.behaviorBaseline.avgDataVolumePerDay} MB</dd>
                            </div>
                          </dl>
                        </div>

                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <button
                            type="button"
                            className="w-full rounded-md bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-100"
                          >
                            <EyeIcon className="h-4 w-4 inline mr-1" />
                            Add to Watchlist
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-medium text-gray-900">Risk Distribution</h3>
                        <p className="text-sm text-gray-500 mt-1">Users by risk level</p>
                        <div className="mt-4 space-y-3">
                          <div className="flex items-center">
                            <div className="w-20 text-sm text-gray-500">Critical</div>
                            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-red-500 rounded-full"
                                style={{ width: `${(ubaDashboard.riskDistribution.critical / ubaDashboard.totalUsers) * 100}%` }}
                              />
                            </div>
                            <div className="w-8 text-right text-sm font-medium text-gray-900">{ubaDashboard.riskDistribution.critical}</div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-20 text-sm text-gray-500">High</div>
                            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-orange-500 rounded-full"
                                style={{ width: `${(ubaDashboard.riskDistribution.high / ubaDashboard.totalUsers) * 100}%` }}
                              />
                            </div>
                            <div className="w-8 text-right text-sm font-medium text-gray-900">{ubaDashboard.riskDistribution.high}</div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-20 text-sm text-gray-500">Medium</div>
                            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-yellow-500 rounded-full"
                                style={{ width: `${(ubaDashboard.riskDistribution.medium / ubaDashboard.totalUsers) * 100}%` }}
                              />
                            </div>
                            <div className="w-8 text-right text-sm font-medium text-gray-900">{ubaDashboard.riskDistribution.medium}</div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-20 text-sm text-gray-500">Low</div>
                            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-green-500 rounded-full"
                                style={{ width: `${(ubaDashboard.riskDistribution.low / ubaDashboard.totalUsers) * 100}%` }}
                              />
                            </div>
                            <div className="w-8 text-right text-sm font-medium text-gray-900">{ubaDashboard.riskDistribution.low}</div>
                          </div>
                        </div>

                        <div className="mt-6">
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Threat Indicators</h4>
                          <div className="space-y-2">
                            {Object.entries(ubaDashboard.threatIndicatorCounts)
                              .filter(([_, count]) => count > 0)
                              .map(([indicator, count]) => (
                                <div key={indicator} className="flex items-center justify-between">
                                  <span className="text-sm text-gray-600 capitalize">{indicator.replace('_', ' ')}</span>
                                  <span className="text-sm font-medium text-red-600">{count}</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Anomalies */}
              <div className="overflow-hidden rounded-lg bg-white shadow">
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Recent Anomalies</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Behavioral anomalies detected across browser, cloud, and identity sources
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                        {ubaDashboard.recentAnomalies.filter(a => a.severity === 'critical').length} Critical
                      </span>
                      <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800">
                        {ubaDashboard.recentAnomalies.filter(a => a.severity === 'high').length} High
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Anomaly</th>
                          <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                          <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                          <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                          <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {ubaDashboard.recentAnomalies.map((anomaly) => (
                          <tr key={anomaly.id} className="hover:bg-gray-50">
                            <td className="py-3">
                              <div>
                                <div className="flex items-center">
                                  {anomaly.type === 'impossible_travel' && <MapPinIcon className="h-4 w-4 text-red-500 mr-2" />}
                                  {anomaly.type === 'data_exfiltration' && <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mr-2" />}
                                  {anomaly.type === 'genai_sensitive_data' && <SparklesIcon className="h-4 w-4 text-purple-500 mr-2" />}
                                  {anomaly.type === 'unusual_access_time' && <ClockIcon className="h-4 w-4 text-yellow-500 mr-2" />}
                                  {anomaly.type === 'privilege_escalation' && <BoltIcon className="h-4 w-4 text-orange-500 mr-2" />}
                                  <span className="text-sm font-medium text-gray-900">
                                    {getAnomalyTypeLabel(anomaly.type)}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{anomaly.description}</p>
                              </div>
                            </td>
                            <td className="py-3">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{anomaly.userName}</p>
                                <p className="text-xs text-gray-500">{anomaly.userDepartment}</p>
                              </div>
                            </td>
                            <td className="py-3">
                              <span className={clsx(
                                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                getRiskLevelColor(anomaly.severity)
                              )}>
                                {anomaly.severity}
                              </span>
                            </td>
                            <td className="py-3">
                              <span className={clsx(
                                'inline-flex items-center rounded px-2 py-0.5 text-xs',
                                anomaly.source === 'browser' ? 'bg-blue-100 text-blue-700' :
                                anomaly.source === 'identity' ? 'bg-purple-100 text-purple-700' :
                                anomaly.source === 'cloud' ? 'bg-cyan-100 text-cyan-700' :
                                'bg-gray-100 text-gray-700'
                              )}>
                                {anomaly.source}
                              </span>
                            </td>
                            <td className="py-3">
                              <span className={clsx(
                                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                getAnomalyStatusColor(anomaly.status)
                              )}>
                                {anomaly.status}
                              </span>
                            </td>
                            <td className="py-3 text-sm text-gray-500">
                              {new Date(anomaly.timestamp).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Insider Threat Summary */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="overflow-hidden rounded-lg bg-white shadow">
                  <div className="p-5">
                    <h3 className="text-lg font-medium text-gray-900">Insider Threat Indicators</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Users showing potential insider threat behaviors
                    </p>
                    <div className="mt-4 space-y-4">
                      {ubaDashboard.topRiskUsers
                        .filter(u => u.threatIndicators.length > 0)
                        .map(user => (
                          <div key={user.userId} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                              <div className={clsx(
                                'h-10 w-10 rounded-full flex items-center justify-center text-white font-medium',
                                user.riskLevel === 'critical' ? 'bg-red-500' : 'bg-orange-500'
                              )}>
                                {user.displayName.charAt(0)}
                              </div>
                              <div className="ml-3">
                                <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
                                <p className="text-xs text-gray-500">{user.department}</p>
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {user.threatIndicators.map((ind, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700"
                                    >
                                      {ind.replace('_', ' ')}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={clsx(
                                'text-xl font-bold',
                                getRiskScoreColor(user.riskScore)
                              )}>
                                {user.riskScore}
                              </span>
                              <p className="text-xs text-gray-500">risk score</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-lg bg-white shadow">
                  <div className="p-5">
                    <h3 className="text-lg font-medium text-gray-900">Browser-Based Detection</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Anomalies detected by Vajra
                    </p>
                    <div className="mt-4 space-y-3">
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center">
                          <GlobeAltIcon className="h-6 w-6 text-blue-600" />
                          <span className="ml-2 text-sm font-medium text-gray-900">GenAI Sensitive Data</span>
                        </div>
                        <span className="text-lg font-bold text-blue-600">
                          {ubaDashboard.recentAnomalies.filter(a => a.type === 'genai_sensitive_data').length}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div className="flex items-center">
                          <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                          <span className="ml-2 text-sm font-medium text-gray-900">Data Exfiltration</span>
                        </div>
                        <span className="text-lg font-bold text-red-600">
                          {ubaDashboard.recentAnomalies.filter(a => a.type === 'data_exfiltration').length}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                        <div className="flex items-center">
                          <MapPinIcon className="h-6 w-6 text-purple-600" />
                          <span className="ml-2 text-sm font-medium text-gray-900">Impossible Travel</span>
                        </div>
                        <span className="text-lg font-bold text-purple-600">
                          {ubaDashboard.recentAnomalies.filter(a => a.type === 'impossible_travel').length}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                        <div className="flex items-center">
                          <ClockIcon className="h-6 w-6 text-yellow-600" />
                          <span className="ml-2 text-sm font-medium text-gray-900">Unusual Access Time</span>
                        </div>
                        <span className="text-lg font-bold text-yellow-600">
                          {ubaDashboard.recentAnomalies.filter(a => a.type === 'unusual_access_time').length}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                        <div className="flex items-center">
                          <BoltIcon className="h-6 w-6 text-orange-600" />
                          <span className="ml-2 text-sm font-medium text-gray-900">Privilege Escalation</span>
                        </div>
                        <span className="text-lg font-bold text-orange-600">
                          {ubaDashboard.recentAnomalies.filter(a => a.type === 'privilege_escalation').length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
